import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Piece, InsertedPiece, PieceSort } from '@/types';

export const usePiecesStore = defineStore('pieces', () => {
	const pieces = ref<Piece[]>([]);
	const allPieces = ref<Piece[]>([]);
	const insertedPieces = ref<InsertedPiece[]>([]);
	const isLoading = ref(false);
	const error = ref<string | null>(null);
	const searchQuery = ref('');
	const selectedTag = ref<string>('');
	const availableTags = ref<string[]>([]);
	const sortBy = ref<PieceSort>('date-desc');

	const isInserted = (pieceId: string): boolean => {
		return insertedPieces.value.some((ip) => ip.pieceId === pieceId);
	};

	const getNumber = (pieceId: string): number | null => {
		const found = insertedPieces.value.find((ip) => ip.pieceId === pieceId);
		return found ? found.number : null;
	};

	// Numéro affiché sur la pièce : numéro Word si insérée, sinon numéro Pappers
	const getDisplayedNumber = (piece: Piece): number | null => getNumber(piece.id) ?? piece.number;

	const getTime = (date: string | null): number | null => {
		if (!date) {
			return null;
		}
		const time = new Date(date).getTime();
		return Number.isNaN(time) ? null : time;
	};

	// Les pièces sans valeur sont toujours reléguées en fin de liste
	const compare = (a: number | null, b: number | null, direction: 1 | -1): number => {
		if (a === null && b === null) {
			return 0;
		} else if (a === null) {
			return 1;
		} else if (b === null) {
			return -1;
		}
		return (a - b) * direction;
	};

	const filteredPieces = computed(() => {
		const sorted = [...pieces.value];
		if (sortBy.value === 'number') {
			sorted.sort((a, b) => compare(getDisplayedNumber(a), getDisplayedNumber(b), 1));
		} else {
			const direction = sortBy.value === 'date-asc' ? 1 : -1;
			sorted.sort((a, b) => compare(getTime(a.date), getTime(b.date), direction));
		}
		return sorted;
	});

	const getOccurrenceCount = (pieceId: string): number => {
		const found = insertedPieces.value.find((ip) => ip.pieceId === pieceId);
		return found ? found.contentControlIds.length : 0;
	};

	const setPieces = (data: Piece[]) => {
		pieces.value = data;
	};

	const setAllPieces = (data: Piece[]) => {
		allPieces.value = data;
	};

	const setInsertedPieces = (data: InsertedPiece[]) => {
		insertedPieces.value = data;
	};

	return {
		pieces,
		allPieces,
		insertedPieces,
		isLoading,
		error,
		searchQuery,
		selectedTag,
		availableTags,
		sortBy,
		filteredPieces,
		isInserted,
		getNumber,
		getOccurrenceCount,
		setPieces,
		setAllPieces,
		setInsertedPieces
	};
});
